import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  Popover,
  Zoom,
  useTheme,
} from '@mui/material';
import {
  FavoriteBorder as HeartOutlineIcon,
  Favorite as HeartFilledIcon,
} from '@mui/icons-material';

// Reaction types with their icons and labels
const REACTIONS = [
  { type: 'amen', emoji: '🙏', label: 'Amen', color: '#D4A574' },
  { type: 'heart', emoji: '❤️', label: 'Love', color: '#E53E3E' },
  { type: 'peace', emoji: '🕊️', label: 'Peace', color: '#63B3ED' },
  { type: 'strength', emoji: '💪', label: 'Strength', color: '#F6AD55' },
  { type: 'inspiration', emoji: '✨', label: 'Inspired', color: '#9F7AEA' },
];

// Get reaction display
export const getReactionEmoji = (type) => {
  const reaction = REACTIONS.find(r => r.type === type);
  return reaction ? reaction.emoji : '🙏';
};

// Get reaction label
export const getReactionLabel = (type) => {
  const reaction = REACTIONS.find(r => r.type === type);
  return reaction ? reaction.label : 'Amen';
};

function ReactionBar({
  reactions = {}, // { amen: 5, heart: 3, peace: 2, strength: 1, inspiration: 0 }
  userReaction = null, // Current user's reaction type or null
  onReact,
  compact = false,
  disabled = false,
}) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoveredReaction, setHoveredReaction] = useState(null);

  const handleClick = (event) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleReaction = (type) => {
    if (onReact) {
      // If user clicks their current reaction, remove it
      if (userReaction === type) {
        onReact(null);
      } else {
        onReact(type);
      }
    }
    handleClose();
  };

  const open = Boolean(anchorEl);

  // Calculate total reactions
  const totalReactions = Object.values(reactions).reduce((sum, count) => sum + count, 0);

  // Get top reactions (non-zero)
  const topReactions = REACTIONS
    .filter(r => reactions[r.type] > 0)
    .sort((a, b) => (reactions[b.type] || 0) - (reactions[a.type] || 0))
    .slice(0, 3);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {/* Main reaction button */}
      <Tooltip title={userReaction ? getReactionLabel(userReaction) : 'Add reaction'}>
        <IconButton
          size="small"
          onClick={handleClick}
          disabled={disabled}
          sx={{
            color: userReaction ? REACTIONS.find(r => r.type === userReaction)?.color : 'text.secondary',
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'scale(1.1)',
              bgcolor: 'rgba(212, 165, 116, 0.1)',
            },
          }}
          aria-label="React to prayer"
        >
          {userReaction ? (
            <Typography component="span" sx={{ fontSize: compact ? '1rem' : '1.25rem' }}>
              {getReactionEmoji(userReaction)}
            </Typography>
          ) : (
            <HeartOutlineIcon fontSize={compact ? 'small' : 'medium'} />
          )}
        </IconButton>
      </Tooltip>

      {/* Reaction summary */}
      {totalReactions > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 },
          }}
          onClick={handleClick}
        >
          {/* Top reaction emojis */}
          <Box sx={{ display: 'flex', ml: -0.5 }}>
            {topReactions.map((reaction, index) => (
              <Typography
                key={reaction.type}
                component="span"
                sx={{
                  fontSize: compact ? '0.75rem' : '0.875rem',
                  ml: index > 0 ? -0.5 : 0,
                  zIndex: topReactions.length - index,
                  position: 'relative',
                }}
              >
                {reaction.emoji}
              </Typography>
            ))}
          </Box>

          {/* Count */}
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              ml: 0.5,
            }}
          >
            {totalReactions}
          </Typography>
        </Box>
      )}

      {/* Reaction picker popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            p: 0.5,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            p: 0.5,
          }}
        >
          {REACTIONS.map((reaction, index) => (
            <Zoom
              key={reaction.type}
              in={open}
              style={{ transitionDelay: open ? `${index * 50}ms` : '0ms' }}
            >
              <Tooltip title={reaction.label} placement="top">
                <IconButton
                  onClick={() => handleReaction(reaction.type)}
                  onMouseEnter={() => setHoveredReaction(reaction.type)}
                  onMouseLeave={() => setHoveredReaction(null)}
                  sx={{
                    width: 44,
                    height: 44,
                    fontSize: '1.5rem',
                    transition: 'all 0.2s ease',
                    bgcolor: userReaction === reaction.type ? `${reaction.color}20` : 'transparent',
                    border: userReaction === reaction.type ? `2px solid ${reaction.color}` : '2px solid transparent',
                    transform: hoveredReaction === reaction.type ? 'scale(1.3)' : 'scale(1)',
                    '&:hover': {
                      bgcolor: `${reaction.color}15`,
                    },
                  }}
                  aria-label={reaction.label}
                >
                  {reaction.emoji}
                </IconButton>
              </Tooltip>
            </Zoom>
          ))}
        </Box>
      </Popover>
    </Box>
  );
}

export default ReactionBar;
export { REACTIONS };
